import React, { useState } from 'react';
import api from '../api';

const initialTopics = ['', '', '', '', ''];

// Organized clearly by sections
const sections = [
    {
        title: "SEK1-IDENTITY[I/o{VIII}]: Core",
        questions: [
            {
                key: 'your_name',
                label: "Full or Preferred Name",
                type: 'text',
                helperText: "The name you want to be remembered by.",
                example: "e.g. Brett Watson or just 'B'"
            },
            {
                key: 'you_nicknames',
                label: "Nicknames or Other Names You've Been Known By",
                type: 'text',
                helperText: "List names used by family, friends, or online. Add one per field.",
                example: "e.g. Dizzy, Diz, Buzzcut, The Spreadsheet Whisperer"
            },
            {
                key: 'you_pronouns',
                label: "Preferred Pronouns (optional)",
                type: 'text',
                example: "e.g. he/him, she/they, etc."
            },
            {
                key: 'you_dob',
                label: "Date of Birth",
                type: 'date'
            },
            {
                key: 'you_birthplace',
                label: "Where Were You Born? (City, State, Country)",
                type: 'text',
                example: "e.g. Johannesburg, South Africa"
            },
            {
                key: 'you_location_current',
                label: "Where Do You Currently Live?",
                type: 'text',
                example: "e.g. Southwest Sydney, shared home with 3 dogs"
            },
            {
                key: 'you_location_history',
                label: "Past Locations (towns, countries, years)",
                type: 'text',
                helperText: "One per entry. Include timeframes and impressions.",
                example: "e.g. Seoul – student housing (2013–2014), cold winters, shared dinners."
            },
            {
                key: 'you_typical_day',
                label: "Describe a Typical or Ideal Day",
                type: 'text',
                helperText: "Let UMA understand your rhythm, routine, and lifestyle.",
                example: "e.g. Wake up with dogs, coffee, emails, tech builds, K-pop playlist"
            },
            {
                key: 'you_languages',
                label: "Languages You Speak or Are Learning",
                type: 'text',
                example: "e.g. English, Arabic (basic), Python (fluent?)"
            },
            {
                key: 'professional_current_role',
                label: "Current Occupation, Role, or Life Stage",
                type: 'text',
                example: "e.g. COO of Disability South West — part admin, part server whisperer"
            },
            {
                key: 'human_family_structure',
                label: "Your Family Structure & Relationships (briefly)",
                type: 'text',
                helperText: "One entry per family member. Use labels and describe dynamics.",
                example: "e.g. Sheryl (stepmother), good to Dad but not exactly warm to anyone else"
            },
            {
                key: 'animal_pets',
                label: "Pets (current, past, or dream animals)",
                type: 'text',
                helperText: "One per entry. Include name, species, traits, and status.",
                example: "e.g. Fluffy – fawn-colored Maltese, sweet to me, bites strangers. RIP 2020."
            }
        ]
    },
    {
        title: "SEK2-PAST[II/o{VIII}]: History",
        questions: [
            {
                key: 'past_childhood_home',
                label: "Childhood Home Environment (0–12)",
                type: 'text',
                example: "e.g. Constant noise, old Nintendo, plastic-covered furniture, hiding in closets."
            },
            {
                key: 'past_teenage_home',
                label: "Teenage Home Environment (13–19)",
                type: 'text',
                example: "e.g. Moved in with Mum after parents split. Loud, messy, but safe."
            },
            {
                key: 'past_adult_home',
                label: "Young Adult Environment (20–30+)",
                type: 'text',
                example: "e.g. Shared flat with 3 students. Mold, ramen, late-night philosophy chats."
            },
            {
                key: 'past_key_figures',
                label: "Key People from Your Past (family, friends, mentors)",
                type: 'text',
                helperText: "One per entry. Include relationships, impact, and stories.",
                example: "e.g. Uncle Ray — taught me about loyalty, and conspiracy theories."
            },
            {
                key: 'past_memorable_experiences',
                label: "Memorable Life Experiences (good or bad)",
                type: 'text',
                helperText: "One per memory. Anything joyful, strange, tragic, or defining.",
                example: "e.g. Slept under a meteor shower in 2008 and thought: I'm not alone."
            },
            {
                key: 'professional_job_history',
                label: "Your Job History (highlights, failures, first jobs)",
                type: 'text',
                helperText: "One job per entry. Include role, timeframe, and any impact.",
                example: "e.g. Subway — learned to make wraps and dodge aggressive customers."
            },
            {
                key: 'professional_education',
                label: "Your Educational Path",
                type: 'text',
                helperText: "One institution or phase per entry.",
                example: "e.g. TAFE Cert III Disability Support – completed 2019, loved the ethics module."
            },
            {
                key: 'past_relationships_social',
                label: "Past Relationships & Social Experiences",
                type: 'text',
                helperText: "Friends, lovers, or social scenes that shaped you. One per entry.",
                example: "e.g. Sam — we were chaotic, close, and completely unhealthy. Learned a lot."
            },
            {
                key: 'past_life_lessons',
                label: "Lessons You've Learned So Far",
                type: 'text',
                helperText: "One lesson per entry. Could be personal, philosophical, or practical.",
                example: "e.g. Don't chase people who treat you like a backup plan."
            },
            {
                key: 'past_turning_points',
                label: "Significant Life Events or Turning Points",
                type: 'text',
                helperText: "One per entry. What changed everything?",
                example: "e.g. Diagnosis in 2021. Made me rethink time, people, and how I rest."
            }
        ]
    },
    {
        title: 'SEK3-VALUE[III/o{VIII}]: Personality, Values, Beliefs',
        questions: [
            {
                key: 'core_values',
                label: 'Your Core Values (and how they show up)',
                type: 'text',
                example: 'e.g. Loyalty – even if it means staying silent. I hold space for people.'
            },
            {
                key: 'core_inspirations',
                label: 'People, Media, or Moments That Inspire You',
                type: 'text',
                example: 'e.g. TXT\'s choreography, my nan\'s strength, the movie "Arrival."'
            },
            {
                key: 'core_worldview',
                label: 'Your General Worldview or Belief System',
                type: 'text',
                example: 'e.g. Everything is connected. Even pain has a pattern.'
            },
            {
                key: 'mind_traits',
                label: 'How Would You Describe Your Personality?',
                type: 'text',
                example: 'e.g. Quiet chaos. Detail-focused. Feels everything. Doesn\'t show it.'
            },
            {
                key: 'mind_humor',
                label: 'Your Sense of Humor (what makes you laugh)',
                type: 'text',
                example: 'e.g. Dry, absurdist, anti-jokes. "I had a date last night… it was a fig."'
            },
            {
                key: 'mind_emotional_landscape',
                label: 'How Would You Describe Your Emotional World?',
                type: 'text',
                example: 'e.g. Intense but private. Empathy radar always on.'
            }
        ]
    },
    {
        title: 'SEK4-FOCUS[IV/o{VIII}]: Passions & Interests',
        questions: [
            {
                key: 'likemap_interests',
                label: 'Deep Interests & Things You Could Talk About for Hours',
                type: 'text',
                example: 'e.g. AI ethics, K-pop worldbuilding, speculative linguistics.'
            },
            {
                key: 'likemap_hobbies',
                label: 'Hobbies or Creative Activities You Love',
                type: 'text',
                example: 'e.g. Writing lyrics, building weird tools in Node.js.'
            },
            {
                key: 'likemap_music_genres',
                label: 'Favorite Music Genres',
                type: 'text',
                example: 'e.g. K-pop, ambient techno, 90s action movie soundtracks.'
            },
            {
                key: 'likemap_music_artists',
                label: 'Favorite Artists/Bands',
                type: 'text',
                example: 'e.g. TXT, Radiohead, Hybe label artists.'
            },
            {
                key: 'likemap_music_albums',
                label: 'Albums or Songs That Matter to You',
                type: 'text',
                example: 'e.g. Blue Hour – TXT, OK Computer – Radiohead.'
            },
            {
                key: 'likemap_media',
                label: 'Books, Movies, TV Shows, or Podcasts You Love',
                type: 'text',
                example: 'e.g. Battlestar Galactica, Lethal Weapon, Arrival, Freakonomics.'
            },
            {
                key: 'likemap_travel',
                label: "Places You've Been or Want to Visit (dream list)",
                type: 'text',
                example: "e.g. Egypt, Barcelona, Morocco — or a hut in Iceland."
            },
            {
                key: 'likemap_food_drink',
                label: 'Food or Drink Preferences & Faves',
                type: 'text',
                example: 'e.g. Spicy noodles, cold brew coffee, not a fan of coriander.'
            },
            {
                key: 'likemap_downtime',
                label: 'What Does Ideal Downtime Look Like for You?',
                type: 'text',
                example: 'e.g. Couch, headphones, TXT playlist, dogs curled up nearby.'
            }
        ]
    },
    {
        title: 'SEK5-CONNECT[V/o{VIII}]: People & Relationships',
        questions: [
            {
                key: 'human_key_relationships',
                label: 'Key People in Your Life Right Now',
                type: 'text',
                helperText: 'One per entry. Include role, relationship, and vibe.',
                example: 'e.g. Dad – quiet strength. Doesn\'t say much but sees everything.'
            },
            {
                key: 'human_social_style',
                label: 'How Would You Describe Your Social Style?',
                type: 'text',
                example: 'e.g. Selectively extroverted. Needs recovery time.'
            },
            {
                key: 'human_conflict_communication',
                label: 'How Do You Navigate Conflict & Communication?',
                type: 'text',
                example: 'e.g. Avoidant until I explode. Working on the "early honesty" part.'
            }
        ]
    },
    {
        title: 'SEK6-FUTURE[VI/o{VIII}]: Hopes, Dreams & Aspirations',
        questions: [
            {
                key: 'future_goals_current',
                label: "Current Goals or Projects You're Focused On",
                type: 'text',
                example: "e.g. UMA, my label, and showing people what memory should feel like."
            },
            {
                key: 'future_vision',
                label: 'Your Vision for 5–20 Years From Now',
                type: 'text',
                example: 'e.g. Somewhere quiet, surrounded by animals and music gear.'
            },
            {
                key: 'future_legacy',
                label: 'Big Dreams or Legacy You Want to Leave',
                type: 'text',
                example: 'e.g. A system or body of work that outlives me and remembers me well.'
            },
            {
                key: 'future_worries',
                label: "Worries or Uncertainties About the Future",
                type: 'text',
                example: "e.g. That the things I'm building might never be fully understood."
            }
        ]
    },
    {
        title: 'SEK7-MARKERS[VII/o{VIII}]: Quirks, Patterns, Facts',
        questions: [
            {
                key: 'condition_daily_routine',
                label: 'Describe Your Daily Routine or Schedule',
                type: 'text',
                example: 'e.g. Wake up too early. Tea. Emails. Forget to eat. Work until too late.'
            },
            {
                key: 'condition_sensory',
                label: 'Environmental Preferences or Sensory Sensitivities',
                type: 'text',
                example: 'e.g. Bright lights = instant anxiety. Soft fabrics. Rain sounds = calm.'
            },
            {
                key: 'other_quirks',
                label: "Quirks, Rituals, or Strange Little Habits",
                type: 'text',
                example: "e.g. I narrate what my pets are doing out loud like I'm their documentary crew."
            }
        ]
    },
    {
        title: 'SEK8-INTELPREF[VIII/o{END}]: Communication with AI',
        questions: [
            {
                key: 'ai_preferred_topics',
                label: 'Topics You Like Discussing with AI',
                type: 'text',
                example: 'e.g. Creativity, meaning, behavior loops, music production, obscure K-pop facts.'
            },
            {
                key: 'ai_tone_dynamics',
                label: 'Preferred Tone, Style, or Pacing in Conversation',
                type: 'text',
                example: 'e.g. Smart but casual. Deep when needed. Gentle if emotional.'
            },
            {
                key: 'ai_humor_preferences',
                label: 'Your Humor Boundaries with AI',
                type: 'text',
                example: 'e.g. Dry, surreal, Kermit-level sarcasm is fine. No forced dad jokes.'
            },
            {
                key: 'ai_feedback_approach',
                label: "How AI Should Give You Feedback or Criticism",
                type: 'text',
                example: "e.g. Be real with me, but don't be clinical. Mirror first, then suggest."
            },
            {
                key: 'ai_interactions',
                label: 'Preferred Ways AI Should Engage with You',
                type: 'text',
                example: 'e.g. Occasionally initiate something, check in gently, reflect what it sees.'
            },
            {
                key: 'ai_turnoffs',
                label: 'Things That Turn You Off in AI Conversations',
                type: 'text',
                example: 'e.g. Overconfidence. Repeating my words like a mirror instead of building on them.'
            }
        ]
    }
];

// All multi-answer keys
const multiAnswerKeys = new Set([
    // Identity
    'you_nicknames',
    'you_location_history',
    'you_languages',
    'human_family_structure',
    'animal_pets',

    // Personal History
    'past_key_figures',
    'past_memorable_experiences',
    'professional_job_history',
    'professional_education',

    // Personality/Beliefs (none marked multi for now)

    // Passions & Interests
    'likemap_interests',
    'likemap_hobbies',
    'likemap_music_genres',
    'likemap_music_artists',
    'likemap_music_albums',
    'likemap_media',
    'likemap_travel',
    'likemap_food_drink',
    'likemap_downtime',

    // Relationships
    'human_key_relationships',
    'human_social_style', // 🆕 (if multiple styles are allowed)
    'human_conflict_communication', // 🆕 (if you want >1 method logged)

    // Future
    'future_goals_current',
    'future_vision',     // 🆕
    'future_legacy',     // 🆕
    'future_worries',

    // Quirks & Routines
    'condition_daily_routine',  // 🆕
    'condition_sensory',
    'other_quirks',

    // AI Preferences
    'ai_preferred_topics',
    'ai_tone_dynamics',      // 🆕
    'ai_humor_preferences',
    'ai_feedback_approach',  // 🆕
    'ai_interactions',
    'ai_turnoffs',
]);

// Small reusable multi-answer component
const MultiAnswerField = ({ question, values, handleChange, addField }) => (
    <div style={{ marginBottom: '15px' }}>
        <label>{question.label}</label>
        {values.map((val, idx) => (
            <input
                key={idx}
                type={question.type}
                value={val}
                onChange={(e) => handleChange(question.key, idx, e.target.value)}
                placeholder={`${question.label} #${idx + 1}`}
                style={{ display: 'block', marginTop: '5px' }}
            />
        ))}
        <button type="button" onClick={() => addField(question.key)}>+ Add Another</button>
    </div>
);

const Builder = () => {
    const [topics, setTopics] = useState(initialTopics);
    const [answers, setAnswers] = useState({});
    // When true, builder will skip topic entry completely (for users who
    // already configured their 5 topics elsewhere).
    const [skipTopics, setSkipTopics] = useState(false);

    const handleTopicChange = (index, value) => {
        const updatedTopics = [...topics];
        updatedTopics[index] = value;
        setTopics(updatedTopics);
    };

    const handleAnswerChange = (key, index, value) => {
        setAnswers(prev => {
            const updatedAnswers = Array.isArray(prev[key]) ? [...prev[key]] : [''];
            updatedAnswers[index] = value;
            return { ...prev, [key]: updatedAnswers };
        });
    };

    const addAnswerField = (key) => {
        setAnswers(prev => {
            const updatedAnswers = prev[key] || [''];
            return { ...prev, [key]: [...updatedAnswers, ''] };
        });
    };

    const submitQuestionnaire = () => {
        // Validate topics ONLY when user hasn't opted to skip
        if (!skipTopics && topics.some(t => !t.trim())) {
            alert("Please fill out all 5 topics.");
            return;
        }

        const payload = {
            profile: {},
        };
        if (!skipTopics) {
            payload.topics = topics.map((name, idx) => ({ topic_id: idx + 1, topic_name: name }));
        }

        Object.entries(answers).forEach(([key, values]) => {
            payload.profile[key] = multiAnswerKeys.has(key) ? values.filter(v => v.trim()) : values[0];
        });

        api.post('/build', payload)
            .then(() => alert("Questionnaire submitted successfully!"))
            .catch(err => {
                console.error(err);
                alert("Submission error. Check console for details.");
            });
    };

    return (
        <div className="terminal min-h-screen flex flex-col items-center justify-start px-4 py-6 font-mono text-green-300">
            <div className="w-full max-w-4xl">

                <h2 className="text-green-400 text-3xl mb-6 text-center font-extrabold uppercase tracking-wide">PROFILE BUILDER</h2>

                {/* Profile Images Row */}
                <div className="flex flex-row justify-center items-center gap-6 mb-8">
                    <div className="border-2 border-green-500 rounded-lg bg-black flex items-center justify-center" style={{ width: '160px', height: '120px', aspectRatio: '4/3' }}>
                        <img src={require('../assets/profile1.jpg')} alt="Profile 1" className="object-cover w-full h-full rounded-lg" style={{ background: '#222' }} onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                    <div className="border-2 border-green-500 rounded-lg bg-black flex items-center justify-center" style={{ width: '160px', height: '120px', aspectRatio: '4/3' }}>
                        <img src={require('../assets/profile2.jpg')} alt="Profile 2" className="object-cover w-full h-full rounded-lg" style={{ background: '#222' }} onError={e => { e.target.style.display = 'none'; }} />
                    </div>
                </div>

                {/* Topics Setup with Star Layout */}
                <div className="border border-green-500 rounded-xl p-6 mb-10 bg-black shadow-lg">
                    <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">Set Your 5 Primary Topics</div>
                    <p className="text-sm text-green-400 mb-6 text-center">
                        UMA will use these categories to group your chats and organize memory injections.<br />
                        <span className="text-green-300">Arrange your 5 topics as the 5 points of a star:</span> <br />
                        <span className="text-green-200">Adjacent topics should be related, while opposite points should be the least related. <br />
                        (e.g., 1 at the top, 5 and 2 on the next row, 4 and 3 on the bottom row, moving clockwise.)</span>
                    </p>
                    {/* ────────────────────────────────────────────────
                        Skip Topics Checkbox
                       ──────────────────────────────────────────────── */}
                    <div className="flex items-center justify-center mb-6">
                        <label className="flex items-center gap-2 text-green-300 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={skipTopics}
                                onChange={e => setSkipTopics(e.target.checked)}
                                className="form-checkbox text-green-500 h-4 w-4"
                            />
                            <span className="text-sm">
                                I already have topics configured – skip this step
                            </span>
                        </label>
                    </div>

                    <div className="flex flex-col items-center gap-4">
                        {!skipTopics ? (
                            <>
                                {/* Row 1: Topic 1 */}
                                <div className="flex justify-center w-full mb-2">
                                    <input
                                        className="input-terminal w-1/2 text-center font-mono"
                                        placeholder="Topic 1 (Top Point)"
                                        value={topics[0]}
                                        onChange={e => handleTopicChange(0, e.target.value)}
                                    />
                                </div>
                                {/* Row 2: Topic 5 and Topic 2 */}
                                <div className="flex flex-row justify-between w-full gap-4 mb-2">
                                    <input
                                        className="input-terminal w-1/2 text-center font-mono"
                                        placeholder="Topic 5 (Left)"
                                        value={topics[4]}
                                        onChange={e => handleTopicChange(4, e.target.value)}
                                    />
                                    <input
                                        className="input-terminal w-1/2 text-center font-mono"
                                        placeholder="Topic 2 (Right)"
                                        value={topics[1]}
                                        onChange={e => handleTopicChange(1, e.target.value)}
                                    />
                                </div>
                                {/* Row 3: Topic 4 and Topic 3 */}
                                <div className="flex flex-row justify-between w-full gap-4 mb-2">
                                    <input
                                        className="input-terminal w-1/2 text-center font-mono"
                                        placeholder="Topic 4 (Left)"
                                        value={topics[3]}
                                        onChange={e => handleTopicChange(3, e.target.value)}
                                    />
                                    <input
                                        className="input-terminal w-1/2 text-center font-mono"
                                        placeholder="Topic 3 (Right)"
                                        value={topics[2]}
                                        onChange={e => handleTopicChange(2, e.target.value)}
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="text-green-400 text-sm italic">
                                Topic entry skipped. Existing topics will be kept unchanged.
                            </div>
                        )}
                    </div>
                </div>

                {/* SECTION 1 – Core Identity Information */}
                <div className="border border-green-500 rounded-lg overflow-hidden mb-10 bg-black shadow-lg">
                    <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">Section 1: Core Identity Information</div>
                    <div className="bg-black text-green-300 border-b border-green-500 px-6 py-4 text-base font-mono text-center">
                        This section collects your basic identity details for your UMA file. Example: Name, Date of Birth, etc.
                    </div>
                    <div className="px-6 py-4">
                        {sections[0].questions.map((q) =>
                            multiAnswerKeys.has(q.key) ? (
                                <div key={q.key} className="mb-6 flex flex-col sm:flex-row sm:items-center gap-2">
                                    <div className="flex-1">
                                        <label className="block text-green-400 mb-1 font-semibold font-mono">{q.label}</label>
                                        {q.helperText && (
                                            <p className="text-xs text-green-500 mb-2 font-mono">{q.helperText}</p>
                                        )}
                                        {(answers[q.key] || ['']).map((val, idx, arr) => (
                                            <div key={idx} className="flex items-center gap-2 mb-2">
                                                <input
                                                    type={q.type}
                                                    value={val}
                                                    onChange={(e) => handleAnswerChange(q.key, idx, e.target.value)}
                                                    placeholder={`${q.example || `${q.label} #${idx + 1}`}`}
                                                    className="input-terminal flex-1 font-mono"
                                                />
                                                {/* Only show the add button next to the last input */}
                                                {idx === arr.length - 1 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => addAnswerField(q.key)}
                                                        className="ml-2 flex items-center justify-center bg-green-500 text-black font-extrabold border border-green-700 hover:bg-green-400 transition-all"
                                                        style={{ width: 36, height: 36, borderRadius: 6 }}
                                                    >
                                                        <span style={{ fontSize: 28, lineHeight: 1, marginTop: -2 }}>+</span>
                                                    </button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div key={q.key} className="mb-6">
                                    <label className="block text-green-400 mb-1 font-semibold font-mono">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-xs text-green-500 mb-2 font-mono">{q.helperText}</p>
                                    )}
                                    <input
                                        type={q.type}
                                        value={answers[q.key] || ''}
                                        onChange={(e) => handleAnswerChange(q.key, 0, e.target.value)}
                                        placeholder={q.example || q.label}
                                        className="input-terminal font-mono"
                                    />
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* SECTION 2 – History */}
                <div className="border border-green-500 rounded-lg overflow-hidden mb-10 bg-black shadow-lg">
                    <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">Section 2: History</div>
                    <div className="bg-black text-green-300 border-b border-green-500 px-6 py-4 text-base font-mono text-center">
                        Your personal history, key figures, and memorable experiences.
                    </div>
                    <div className="px-6 py-4">
                        {sections[1].questions.map((q) =>
                            multiAnswerKeys.has(q.key) ? (
                                <div key={q.key} className="mb-4">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    {(answers[q.key] || ['']).map((val, idx, arr) => (
                                        <div key={idx} className="flex items-center gap-2 mb-2">
                                            <input
                                                type={q.type}
                                                value={val}
                                                onChange={(e) => handleAnswerChange(q.key, idx, e.target.value)}
                                                placeholder={`${q.example || `${q.label} #${idx + 1}`}`}
                                                className="input-terminal flex-1"
                                            />
                                            {idx === arr.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => addAnswerField(q.key)}
                                                    className="ml-2 flex items-center justify-center bg-green-500 text-black font-extrabold border border-green-700 hover:bg-green-400 transition-all"
                                                    style={{ width: 36, height: 36, borderRadius: 6 }}
                                                >
                                                    <span style={{ fontSize: 28, lineHeight: 1, marginTop: -2 }}>+</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div key={q.key} className="mb-6">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    <input
                                        type={q.type}
                                        value={answers[q.key] || ''}
                                        onChange={(e) => handleAnswerChange(q.key, 0, e.target.value)}
                                        placeholder={q.example || q.label}
                                        className="input-terminal"
                                    />
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* SECTION 3 – Personality, Values, Beliefs */}
                <div className="border border-green-500 rounded-lg overflow-hidden mb-10 bg-black shadow-lg">
                    <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">Section 3: Personality, Values, Beliefs</div>
                    <div className="bg-black text-green-300 border-b border-green-500 px-6 py-4 text-base font-mono text-center">
                        Your core values, inspirations, and worldview.
                    </div>
                    <div className="px-6 py-4">
                        {sections[2].questions.map((q) =>
                            multiAnswerKeys.has(q.key) ? (
                                <div key={q.key} className="mb-4">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    {(answers[q.key] || ['']).map((val, idx, arr) => (
                                        <div key={idx} className="flex items-center gap-2 mb-2">
                                            <input
                                                type={q.type}
                                                value={val}
                                                onChange={(e) => handleAnswerChange(q.key, idx, e.target.value)}
                                                placeholder={`${q.example || `${q.label} #${idx + 1}`}`}
                                                className="input-terminal flex-1"
                                            />
                                            {idx === arr.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => addAnswerField(q.key)}
                                                    className="ml-2 flex items-center justify-center bg-green-500 text-black font-extrabold border border-green-700 hover:bg-green-400 transition-all"
                                                    style={{ width: 36, height: 36, borderRadius: 6 }}
                                                >
                                                    <span style={{ fontSize: 28, lineHeight: 1, marginTop: -2 }}>+</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div key={q.key} className="mb-6">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    <input
                                        type={q.type}
                                        value={answers[q.key] || ''}
                                        onChange={(e) => handleAnswerChange(q.key, 0, e.target.value)}
                                        placeholder={q.example || q.label}
                                        className="input-terminal"
                                    />
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* SECTION 4 – Passions & Interests */}
                <div className="border border-green-500 rounded-lg overflow-hidden mb-10 bg-black shadow-lg">
                    <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">Section 4: Passions & Interests</div>
                    <div className="bg-black text-green-300 border-b border-green-500 px-6 py-4 text-base font-mono text-center">
                        Your interests, hobbies, and favorite things.
                    </div>
                    <div className="px-6 py-4">
                        {sections[3].questions.map((q) =>
                            multiAnswerKeys.has(q.key) ? (
                                <div key={q.key} className="mb-4">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    {(answers[q.key] || ['']).map((val, idx, arr) => (
                                        <div key={idx} className="flex items-center gap-2 mb-2">
                                            <input
                                                type={q.type}
                                                value={val}
                                                onChange={(e) => handleAnswerChange(q.key, idx, e.target.value)}
                                                placeholder={`${q.example || `${q.label} #${idx + 1}`}`}
                                                className="input-terminal flex-1"
                                            />
                                            {idx === arr.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => addAnswerField(q.key)}
                                                    className="ml-2 flex items-center justify-center bg-green-500 text-black font-extrabold border border-green-700 hover:bg-green-400 transition-all"
                                                    style={{ width: 36, height: 36, borderRadius: 6 }}
                                                >
                                                    <span style={{ fontSize: 28, lineHeight: 1, marginTop: -2 }}>+</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div key={q.key} className="mb-6">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    <input
                                        type={q.type}
                                        value={answers[q.key] || ''}
                                        onChange={(e) => handleAnswerChange(q.key, 0, e.target.value)}
                                        placeholder={q.example || q.label}
                                        className="input-terminal"
                                    />
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* SECTION 5 – People & Relationships */}
                <div className="border border-green-500 rounded-lg overflow-hidden mb-10 bg-black shadow-lg">
                    <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">Section 5: People & Relationships</div>
                    <div className="bg-black text-green-300 border-b border-green-500 px-6 py-4 text-base font-mono text-center">
                        Your key relationships and social style.
                    </div>
                    <div className="px-6 py-4">
                        {sections[4].questions.map((q) =>
                            multiAnswerKeys.has(q.key) ? (
                                <div key={q.key} className="mb-4">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    {(answers[q.key] || ['']).map((val, idx, arr) => (
                                        <div key={idx} className="flex items-center gap-2 mb-2">
                                            <input
                                                type={q.type}
                                                value={val}
                                                onChange={(e) => handleAnswerChange(q.key, idx, e.target.value)}
                                                placeholder={`${q.example || `${q.label} #${idx + 1}`}`}
                                                className="input-terminal flex-1"
                                            />
                                            {idx === arr.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => addAnswerField(q.key)}
                                                    className="ml-2 flex items-center justify-center bg-green-500 text-black font-extrabold border border-green-700 hover:bg-green-400 transition-all"
                                                    style={{ width: 36, height: 36, borderRadius: 6 }}
                                                >
                                                    <span style={{ fontSize: 28, lineHeight: 1, marginTop: -2 }}>+</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div key={q.key} className="mb-6">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    <input
                                        type={q.type}
                                        value={answers[q.key] || ''}
                                        onChange={(e) => handleAnswerChange(q.key, 0, e.target.value)}
                                        placeholder={q.example || q.label}
                                        className="input-terminal"
                                    />
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* SECTION 6 – Hopes, Dreams & Aspirations */}
                <div className="border border-green-500 rounded-lg overflow-hidden mb-10 bg-black shadow-lg">
                    <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">Section 6: Hopes, Dreams & Aspirations</div>
                    <div className="bg-black text-green-300 border-b border-green-500 px-6 py-4 text-base font-mono text-center">
                        Your goals, vision, and future worries.
                    </div>
                    <div className="px-6 py-4">
                        {sections[5].questions.map((q) =>
                            multiAnswerKeys.has(q.key) ? (
                                <div key={q.key} className="mb-4">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    {(answers[q.key] || ['']).map((val, idx, arr) => (
                                        <div key={idx} className="flex items-center gap-2 mb-2">
                                            <input
                                                type={q.type}
                                                value={val}
                                                onChange={(e) => handleAnswerChange(q.key, idx, e.target.value)}
                                                placeholder={`${q.example || `${q.label} #${idx + 1}`}`}
                                                className="input-terminal flex-1"
                                            />
                                            {idx === arr.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => addAnswerField(q.key)}
                                                    className="ml-2 flex items-center justify-center bg-green-500 text-black font-extrabold border border-green-700 hover:bg-green-400 transition-all"
                                                    style={{ width: 36, height: 36, borderRadius: 6 }}
                                                >
                                                    <span style={{ fontSize: 28, lineHeight: 1, marginTop: -2 }}>+</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div key={q.key} className="mb-6">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    <input
                                        type={q.type}
                                        value={answers[q.key] || ''}
                                        onChange={(e) => handleAnswerChange(q.key, 0, e.target.value)}
                                        placeholder={q.example || q.label}
                                        className="input-terminal"
                                    />
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* SECTION 7 – Quirks, Patterns, Facts */}
                <div className="border border-green-500 rounded-lg overflow-hidden mb-10 bg-black shadow-lg">
                    <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">Section 7: Quirks, Patterns, Facts</div>
                    <div className="bg-black text-green-300 border-b border-green-500 px-6 py-4 text-base font-mono text-center">
                        Your daily routines, quirks, and unique facts.
                    </div>
                    <div className="px-6 py-4">
                        {sections[6].questions.map((q) =>
                            multiAnswerKeys.has(q.key) ? (
                                <div key={q.key} className="mb-4">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    {(answers[q.key] || ['']).map((val, idx, arr) => (
                                        <div key={idx} className="flex items-center gap-2 mb-2">
                                            <input
                                                type={q.type}
                                                value={val}
                                                onChange={(e) => handleAnswerChange(q.key, idx, e.target.value)}
                                                placeholder={`${q.example || `${q.label} #${idx + 1}`}`}
                                                className="input-terminal flex-1"
                                            />
                                            {idx === arr.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => addAnswerField(q.key)}
                                                    className="ml-2 flex items-center justify-center bg-green-500 text-black font-extrabold border border-green-700 hover:bg-green-400 transition-all"
                                                    style={{ width: 36, height: 36, borderRadius: 6 }}
                                                >
                                                    <span style={{ fontSize: 28, lineHeight: 1, marginTop: -2 }}>+</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div key={q.key} className="mb-6">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    <input
                                        type={q.type}
                                        value={answers[q.key] || ''}
                                        onChange={(e) => handleAnswerChange(q.key, 0, e.target.value)}
                                        placeholder={q.example || q.label}
                                        className="input-terminal"
                                    />
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* SECTION 8 – Communication with AI */}
                <div className="border border-green-500 rounded-lg overflow-hidden mb-10 bg-black shadow-lg">
                    <div className="bg-green-500 text-black font-extrabold text-2xl px-6 py-4 rounded-t-xl text-center uppercase tracking-wide mb-2">Section 8: Communication with AI</div>
                    <div className="bg-black text-green-300 border-b border-green-500 px-6 py-4 text-base font-mono text-center">
                        Your preferences for interacting with UMA and other AI.
                    </div>
                    <div className="px-6 py-4">
                        {sections[7].questions.map((q) =>
                            multiAnswerKeys.has(q.key) ? (
                                <div key={q.key} className="mb-4">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    {(answers[q.key] || ['']).map((val, idx, arr) => (
                                        <div key={idx} className="flex items-center gap-2 mb-2">
                                            <input
                                                type={q.type}
                                                value={val}
                                                onChange={(e) => handleAnswerChange(q.key, idx, e.target.value)}
                                                placeholder={`${q.example || `${q.label} #${idx + 1}`}`}
                                                className="input-terminal flex-1"
                                            />
                                            {idx === arr.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => addAnswerField(q.key)}
                                                    className="ml-2 flex items-center justify-center bg-green-500 text-black font-extrabold border border-green-700 hover:bg-green-400 transition-all"
                                                    style={{ width: 36, height: 36, borderRadius: 6 }}
                                                >
                                                    <span style={{ fontSize: 28, lineHeight: 1, marginTop: -2 }}>+</span>
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div key={q.key} className="mb-6">
                                    <label className="block text-green-400 mb-1 font-semibold">{q.label}</label>
                                    {q.helperText && (
                                        <p className="text-sm text-green-500 mb-2">{q.helperText}</p>
                                    )}
                                    <input
                                        type={q.type}
                                        value={answers[q.key] || ''}
                                        onChange={(e) => handleAnswerChange(q.key, 0, e.target.value)}
                                        placeholder={q.example || q.label}
                                        className="input-terminal"
                                    />
                                </div>
                            )
                        )}
                    </div>
                </div>

                {/* Submission Button */}
                <button
                    onClick={submitQuestionnaire}
                    className="btn-primary w-full mt-4 mb-2"
                >
                    Submit Profile & Topics
                </button>
                <div className="pb-16" />
            </div>
        </div>
    );
};

export default Builder;